import {
  DEFAULT_SERVICE_JOB_DETAIL_COLUMN_IDS,
  LOCATION_OVERRIDE_REASONS,
  type NewReportPreview,
  REPORT_ALL_TIME_FROM,
  REPORT_ALL_TIME_TO,
  REPORT_PAYMENT_STATES,
  type ReportColumnLocale,
  type ReportPaymentState,
  SERVICE_JOB_DETAIL_COLUMN_IDS,
  type ServiceJobDetailColumnId,
  TRIP_STATUSES,
  type TripStatus,
} from '@cabgo/shared';
import { type DateRangeValue, isIsoDay, rangeForShortcut, rangeToExclusiveIso } from '../../lib/dates';
import type { ReportViewDefinition } from './reportViewTypes';

export const REPORT_PAGE_SIZE = 20;
export const SERVICE_JOB_DETAIL_SORTS = ['tripRef', 'serviceDate', 'status', 'createdAt'] as const;
export type ServiceJobDetailSort = (typeof SERVICE_JOB_DETAIL_SORTS)[number];

export interface ReportParams {
  providerIds: string[];
  serviceAreaIds: string[];
  serviceGroupIds: string[];
  statuses: TripStatus[];
  paymentStates: ReportPaymentState[];
  hasLocationException?: 'true' | 'false';
  latestExceptionWarnings?: string[];
  latestExceptionOverrideReasons?: string[];
  q: string;
  dateRange: DateRangeValue;
  page: number;
  sort: ServiceJobDetailSort;
  order: 'asc' | 'desc';
  columns: ServiceJobDetailColumnId[];
}

function csv(value: string | null): string[] {
  return [
    ...new Set(
      (value ?? '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}

function allowedValues<T extends string>(value: string | null, allowed: readonly T[]): T[] {
  return csv(value).filter((item): item is T => allowed.includes(item as T));
}

export function canonicalizeReportColumns(value: string | null): ServiceJobDetailColumnId[] {
  if (value === null) return [...DEFAULT_SERVICE_JOB_DETAIL_COLUMN_IDS];
  const selected = new Set(csv(value));
  const columns = SERVICE_JOB_DETAIL_COLUMN_IDS.filter((id) => selected.has(id));
  return columns.length ? columns : [...DEFAULT_SERVICE_JOB_DETAIL_COLUMN_IDS];
}

export function reportColumnsParam(columns: readonly ServiceJobDetailColumnId[]): string | null {
  return columns.length === DEFAULT_SERVICE_JOB_DETAIL_COLUMN_IDS.length &&
    columns.every((id, index) => id === DEFAULT_SERVICE_JOB_DETAIL_COLUMN_IDS[index])
    ? null
    : columns.join(',');
}

export function parseReportParams(params: URLSearchParams): ReportParams {
  const sort = params.get('sort');
  const from = params.get('from');
  const to = params.get('to');
  return {
    providerIds: csv(params.get('provider')),
    serviceAreaIds: csv(params.get('area')),
    serviceGroupIds: csv(params.get('group')),
    statuses: allowedValues(params.get('status'), TRIP_STATUSES),
    paymentStates: allowedValues(params.get('payment'), REPORT_PAYMENT_STATES),
    hasLocationException:
      params.get('exception') === 'true' ? 'true' : params.get('exception') === 'false' ? 'false' : undefined,
    latestExceptionWarnings: allowedValues(params.get('latestWarning'), [
      'OUTSIDE_RADIUS',
      'LOCATION_UNAVAILABLE',
      'LOW_ACCURACY',
    ]),
    latestExceptionOverrideReasons: allowedValues(params.get('latestOverride'), LOCATION_OVERRIDE_REASONS),
    q: params.get('q')?.trim() ?? '',
    dateRange: { from: isIsoDay(from) ? from : null, to: isIsoDay(to) ? to : null },
    page: Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1),
    sort: SERVICE_JOB_DETAIL_SORTS.includes(sort as ServiceJobDetailSort)
      ? (sort as ServiceJobDetailSort)
      : 'serviceDate',
    order: params.get('order') === 'asc' ? 'asc' : 'desc',
    columns: canonicalizeReportColumns(params.get('columns')),
  };
}

export type ReportParamsPatch = Partial<
  Pick<
    ReportParams,
    | 'providerIds'
    | 'serviceAreaIds'
    | 'serviceGroupIds'
    | 'statuses'
    | 'paymentStates'
    | 'hasLocationException'
    | 'latestExceptionWarnings'
    | 'latestExceptionOverrideReasons'
    | 'q'
    | 'dateRange'
    | 'page'
    | 'sort'
    | 'order'
    | 'columns'
  >
>;

export function updateReportParams(current: URLSearchParams, patch: ReportParamsPatch): URLSearchParams {
  const next = new URLSearchParams(current);
  const setCsv = (key: string, values: readonly string[] | undefined) => {
    if (values === undefined) return;
    if (values.length) next.set(key, values.join(','));
    else next.delete(key);
  };
  setCsv('provider', patch.providerIds);
  setCsv('area', patch.serviceAreaIds);
  setCsv('group', patch.serviceGroupIds);
  setCsv('status', patch.statuses);
  setCsv('payment', patch.paymentStates);
  setCsv('latestWarning', patch.latestExceptionWarnings);
  setCsv('latestOverride', patch.latestExceptionOverrideReasons);
  if ('hasLocationException' in patch) {
    if (patch.hasLocationException) next.set('exception', patch.hasLocationException);
    else next.delete('exception');
  }
  if (patch.columns !== undefined) {
    const value = reportColumnsParam(patch.columns);
    if (value) next.set('columns', value);
    else next.delete('columns');
  }
  if (patch.q !== undefined) {
    if (patch.q.trim()) next.set('q', patch.q.trim());
    else next.delete('q');
  }
  if (patch.dateRange !== undefined) {
    if (patch.dateRange.from) next.set('from', patch.dateRange.from);
    else next.delete('from');
    if (patch.dateRange.to) next.set('to', patch.dateRange.to);
    else next.delete('to');
  }
  if (patch.sort !== undefined) {
    if (patch.sort === 'serviceDate') next.delete('sort');
    else next.set('sort', patch.sort);
  }
  if (patch.order !== undefined) {
    if (patch.order === 'desc') next.delete('order');
    else next.set('order', patch.order);
  }
  if (patch.page !== undefined) {
    if (patch.page <= 1) next.delete('page');
    else next.set('page', String(patch.page));
  } else if (patch.columns === undefined) {
    next.delete('page');
  }
  if (next.get('sort') === 'serviceDate') next.delete('sort');
  if (next.get('order') === 'desc') next.delete('order');
  return next;
}

export function buildServiceJobDetailPreviewPath(params: ReportParams): string {
  const query = new URLSearchParams({
    limit: String(REPORT_PAGE_SIZE),
    offset: String((params.page - 1) * REPORT_PAGE_SIZE),
    sort: params.sort,
    order: params.order,
  });
  const dateRange = rangeToExclusiveIso(params.dateRange);
  if (dateRange.from) query.set('from', dateRange.from);
  if (dateRange.to) query.set('to', dateRange.to);
  if (params.providerIds.length) query.set('providerId', params.providerIds.join(','));
  if (params.serviceAreaIds.length) query.set('serviceAreaId', params.serviceAreaIds.join(','));
  if (params.serviceGroupIds.length) query.set('serviceGroupId', params.serviceGroupIds.join(','));
  if (params.statuses.length) query.set('status', params.statuses.join(','));
  if (params.paymentStates.length) query.set('paymentState', params.paymentStates.join(','));
  if (params.hasLocationException) query.set('hasLocationException', params.hasLocationException);
  if (params.latestExceptionWarnings?.length)
    query.set('latestExceptionWarning', params.latestExceptionWarnings.join(','));
  if (params.latestExceptionOverrideReasons?.length)
    query.set('latestExceptionOverrideReason', params.latestExceptionOverrideReasons.join(','));
  if (params.q) query.set('q', params.q);
  return `/reports/service_job_detail/preview?${query.toString()}`;
}

export type ServiceJobDetailExportBody = Record<string, string | ServiceJobDetailColumnId[]>;

export function buildServiceJobDetailExportBody(
  params: ReportParams,
  columns: readonly ServiceJobDetailColumnId[] = params.columns,
  locale: ReportColumnLocale = 'en',
): ServiceJobDetailExportBody {
  const body: ServiceJobDetailExportBody = {
    sort: params.sort,
    order: params.order,
    columns: [...columns],
    locale,
  };
  const dateRange = rangeToExclusiveIso(params.dateRange);
  if (dateRange.from) body.from = dateRange.from;
  if (dateRange.to) body.to = dateRange.to;
  if (params.providerIds.length) body.providerId = params.providerIds.join(',');
  if (params.serviceAreaIds.length) body.serviceAreaId = params.serviceAreaIds.join(',');
  if (params.serviceGroupIds.length) body.serviceGroupId = params.serviceGroupIds.join(',');
  if (params.statuses.length) body.status = params.statuses.join(',');
  if (params.paymentStates.length) body.paymentState = params.paymentStates.join(',');
  if (params.hasLocationException) body.hasLocationException = params.hasLocationException;
  if (params.latestExceptionWarnings?.length)
    body.latestExceptionWarning = params.latestExceptionWarnings.join(',');
  if (params.latestExceptionOverrideReasons?.length)
    body.latestExceptionOverrideReason = params.latestExceptionOverrideReasons.join(',');
  if (params.q) body.q = params.q;
  return body;
}

export interface TableReportParams {
  providerIds: string[];
  serviceAreaIds: string[];
  serviceGroupIds: string[];
  dateRange: DateRangeValue;
  dateMode: ReportViewDefinition['dateMode'];
  businessDate?: string;
  q: string;
  page: number;
  sort: string;
  order: 'asc' | 'desc';
  columns: string[];
  specific: Record<string, string | string[] | boolean>;
}

export function canonicalTableColumns(value: string | null, definition: ReportViewDefinition): string[] {
  const selected = new Set(value === null ? definition.defaultColumns : csv(value));
  const columns = definition.columns.filter((column) => selected.has(column.id)).map((column) => column.id);
  return columns.length ? columns : [...definition.defaultColumns];
}

export function parseTableReportParams(
  params: URLSearchParams,
  definition: ReportViewDefinition,
  businessDate?: string,
): TableReportParams {
  const from = params.get('from');
  const to = params.get('to');
  const explicitRange = params.has('from') || params.has('to') || params.get('range') === 'all';
  const dateRange =
    definition.dateMode === 'today'
      ? { from: null, to: null }
      : explicitRange || definition.dateMode === 'allTime'
        ? { from: isIsoDay(from) ? from : null, to: isIsoDay(to) ? to : null }
        : rangeForShortcut('last30');
  const specific: TableReportParams['specific'] = {};
  for (const filter of definition.filters) {
    if (definition.dateMode === 'today' && filter.id === 'businessDate') continue;
    const value = params.get(filter.id);
    if (!value) continue;
    const allowed = filter.values ?? filter.options?.map((option) => option.value);
    if (filter.type === 'multi' || filter.type === 'uuidMulti') {
      const values = allowed ? allowedValues(value, allowed) : csv(value);
      if (values.length) specific[filter.id] = values;
    } else if (filter.type === 'boolean') {
      if (value === 'true' || value === 'false') specific[filter.id] = value === 'true';
    } else if (!allowed || allowed.includes(value)) specific[filter.id] = value;
  }
  const sort = params.get('sort') ?? definition.defaultSort;
  return {
    providerIds: csv(params.get('provider')),
    serviceAreaIds: csv(params.get('area')),
    serviceGroupIds: csv(params.get('group')),
    dateRange,
    dateMode: definition.dateMode,
    businessDate,
    q: definition.supportsSearch ? (params.get('q')?.trim() ?? '') : '',
    page: Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1),
    sort: definition.sorts.includes(sort) ? sort : definition.defaultSort,
    order: params.get('order') === 'asc' ? 'asc' : 'desc',
    columns: canonicalTableColumns(params.get('columns'), definition),
    specific,
  };
}

export function updateTableReportParams(
  current: URLSearchParams,
  patch: Partial<TableReportParams>,
  definition: ReportViewDefinition,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const [field, key] of [
    ['providerIds', 'provider'],
    ['serviceAreaIds', 'area'],
    ['serviceGroupIds', 'group'],
  ] as const) {
    const values = patch[field];
    if (values === undefined) continue;
    if (values.length) next.set(key, values.join(','));
    else next.delete(key);
  }
  if (patch.dateRange) {
    for (const key of ['from', 'to'] as const) {
      if (patch.dateRange[key]) next.set(key, patch.dateRange[key]!);
      else next.delete(key);
    }
    if (!patch.dateRange.from && !patch.dateRange.to) next.set('range', 'all');
    else next.delete('range');
  }
  if (patch.specific) {
    for (const filter of definition.filters) {
      const value = patch.specific[filter.id];
      if (value === undefined || value === '' || (Array.isArray(value) && !value.length))
        next.delete(filter.id);
      else next.set(filter.id, Array.isArray(value) ? value.join(',') : String(value));
    }
  }
  if (patch.columns) {
    const columns = canonicalTableColumns(patch.columns.join(','), definition);
    if (columns.join(',') === definition.defaultColumns.join(',')) next.delete('columns');
    else next.set('columns', columns.join(','));
  }
  if (patch.q !== undefined) {
    if (patch.q.trim()) next.set('q', patch.q.trim());
    else next.delete('q');
  }
  if (patch.sort !== undefined) {
    if (patch.sort === definition.defaultSort) next.delete('sort');
    else next.set('sort', patch.sort);
  }
  if (patch.order !== undefined) {
    if (patch.order === 'desc') next.delete('order');
    else next.set('order', patch.order);
  }
  if (patch.page !== undefined && patch.page > 1) next.set('page', String(patch.page));
  else if (!patch.columns) next.delete('page');
  return next;
}

export type TableReportExportBody = Record<string, string | string[]>;

export function buildTableReportExportBody(
  params: TableReportParams,
  locale: ReportColumnLocale,
  todayPreview?: Pick<NewReportPreview, 'businessDate' | 'resolvedRange'>,
): TableReportExportBody {
  const body: TableReportExportBody = {
    sort: params.sort,
    order: params.order,
    columns: params.columns,
    locale,
  };
  const bounds = rangeToExclusiveIso(params.dateRange);
  if (params.dateMode !== 'today') {
    body.from = bounds.from ?? REPORT_ALL_TIME_FROM;
    body.to = bounds.to ?? REPORT_ALL_TIME_TO;
  } else if (todayPreview?.resolvedRange) {
    body.from = todayPreview.resolvedRange.from;
    body.to = todayPreview.resolvedRange.to;
  }
  if (params.providerIds.length) body.providerId = params.providerIds.join(',');
  if (params.serviceAreaIds.length) body.serviceAreaId = params.serviceAreaIds.join(',');
  if (params.serviceGroupIds.length) body.serviceGroupId = params.serviceGroupIds.join(',');
  if (params.q) body.q = params.q;
  for (const [key, value] of Object.entries(params.specific))
    body[key] = Array.isArray(value) ? value.join(',') : String(value);
  if (params.dateMode === 'today' && todayPreview?.businessDate)
    body.businessDate = todayPreview.businessDate;
  else if (params.businessDate) body.businessDate = params.businessDate;
  return body;
}

export function buildTableReportPreviewPath(
  params: TableReportParams,
  definition: ReportViewDefinition,
): string {
  const body = buildTableReportExportBody(params, 'en');
  const query = new URLSearchParams({
    limit: String(REPORT_PAGE_SIZE),
    offset: String((params.page - 1) * REPORT_PAGE_SIZE),
  });
  for (const [key, value] of Object.entries(body)) {
    if (key !== 'columns' && key !== 'locale') query.set(key, String(value));
  }
  return `/reports/${definition.key}/preview?${query}`;
}
